import React, { useEffect, useState } from 'react';
import { Prospect } from '../../shared/types';
import { launchProjectsData } from '../../shared/data/imobiliariaData';
import { Building, MapPin, Calendar, Layers, ShieldCheck, CheckCircle2, Phone, ArrowRight, Sparkles } from 'lucide-react';

export const RealEstateHotpage: React.FC<{ prospect: Prospect }> = ({ prospect }) => {
  const project = launchProjectsData[0];
  const [selectedPlan, setSelectedPlan] = useState(0);
  const [reservationDone, setReservationDone] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A] font-sans selection:bg-[#1E3A8A] selection:text-white">
      
      {/* Header Hotpage - STRICT NO SHADOWS */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
             <div className="w-10 h-10 rounded-xl bg-[#1E3A8A] text-white flex items-center justify-center font-black">
                <Building className="w-5 h-5" />
             </div>
             <div>
                <span className="text-[10px] text-emerald-600 font-bold uppercase tracking-widest block">Lançamento de Alto Padrão</span>
                <h1 className="font-heading font-black text-xl text-[#0F172A] leading-none">{project.title}</h1>
             </div>
          </div>

          <div className="flex items-center gap-4">
             <span className="hidden md:block text-xs font-bold text-slate-500">Entrega: <strong className="text-[#0F172A]">{project.deliveryDate}</strong></span>
             <a href="#reserva" className="bg-[#10B981] hover:bg-emerald-600 text-[#0F172A] px-5 py-2.5 rounded-xl font-black text-xs uppercase tracking-wider border border-emerald-500 transition-colors">
                Garantir Unidade VIP
             </a>
          </div>
        </div>
      </header>

      {/* Hero Imersivo Ámbar / Gaia 53 Style - STRICT NO SHADOWS */}
      <section className="relative py-20 px-6 bg-slate-900 text-white overflow-hidden">
        <img src={project.heroImage} alt="Empreendimento" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-900/80 to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
           <div className="lg:col-span-7 space-y-6">
              <span className="bg-[#10B981] text-black text-xs font-black uppercase px-3 py-1.5 rounded-md inline-block">
                 {project.status} • {project.builder}
              </span>
              <h2 className="text-4xl lg:text-6xl font-heading font-black tracking-tight leading-tight">
                 {project.title} <br />
                 <span className="text-[#C5A880] font-serif font-normal italic">
                    {project.tagline}
                 </span>
              </h2>
              <p className="text-sm text-slate-300 font-light max-w-xl leading-relaxed">
                 Localização privilegiada no bairro {project.neighborhood} em {project.city}. Apartamentos de {project.areaRange} com plantas inteligentes de {project.bedroomRange}.
              </p>

              <div className="flex flex-wrap gap-4 pt-2 text-xs font-bold">
                 <div className="bg-white/10 px-4 py-2.5 rounded-xl border border-white/20">📍 {project.neighborhood}, {project.city}</div>
                 <div className="bg-white/10 px-4 py-2.5 rounded-xl border border-white/20">📐 {project.areaRange}</div>
                 <div className="bg-white/10 px-4 py-2.5 rounded-xl border border-white/20">🔑 A partir de R$ 890.000</div>
              </div>
           </div>

           {/* Form de Pré-Reserva VIP */}
           <div id="reserva" className="lg:col-span-5 bg-white text-[#0F172A] p-8 rounded-2xl border border-slate-300 space-y-4">
              <h3 className="font-heading font-black text-xl text-[#0F172A]">Reserva de Unidade VIP</h3>
              <p className="text-xs text-slate-500">Cadastre-se para receber a tabela de preços de lançamento com condições exclusivas de pagamento.</p>

              {reservationDone ? (
                 <div className="bg-emerald-50 border border-emerald-200 p-6 rounded-xl text-center space-y-2">
                    <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto" />
                    <h4 className="font-bold text-sm text-emerald-900">Cadastro VIP Confirmado!</h4>
                    <p className="text-xs text-emerald-700">Nosso consultor de lançamentos entrará em contato via WhatsApp com o book do empreendimento.</p>
                 </div>
              ) : (
                 <form onSubmit={(e) => { e.preventDefault(); setReservationDone(true); }} className="space-y-3">
                    <input type="text" placeholder="Seu Nome Completo" required className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-xs outline-none" />
                    <input type="tel" placeholder="Seu WhatsApp com DDD" required className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-xs outline-none" />
                    <select className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-xs outline-none text-slate-700">
                       <option>Interesse: Planta 3 Suítes (142m²)</option>
                       <option>Interesse: Penthouse Duplex (245m²)</option>
                    </select>
                    <button type="submit" className="w-full bg-[#1E3A8A] text-white py-4 rounded-xl font-bold text-xs uppercase tracking-wider hover:bg-blue-900 transition-colors">
                       Solicitar Tabela de Lançamento
                    </button>
                 </form>
              )}
           </div>
        </div>
      </section>

      {/* Plantas Baixas Interativas (Ámbar Style) - STRICT NO SHADOWS */}
      <section className="py-20 px-6 max-w-7xl mx-auto">
         <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs font-bold text-[#1E3A8A] uppercase tracking-widest">Plantas Humanizadas</span>
            <h3 className="text-3xl font-heading font-black text-[#0F172A] mt-1">Conheça os Layouts do Empreendimento</h3>
         </div>

         <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-4 space-y-3">
               {project.floorPlans.map((plan, idx) => (
                  <button
                     key={idx}
                     onClick={() => setSelectedPlan(idx)}
                     className={`w-full p-4 rounded-2xl border text-left transition-colors ${selectedPlan === idx ? 'bg-[#1E3A8A] text-white border-blue-900' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'}`}
                  >
                     <h4 className="font-bold text-sm">{plan.title}</h4>
                     <p className="text-xs opacity-80 mt-1">Área Privativa: {plan.area}</p>
                  </button>
               ))}
            </div>

            <div className="lg:col-span-8 bg-white p-6 rounded-2xl border border-slate-200">
               <img src={project.floorPlans[selectedPlan].image} alt="Planta" className="w-full h-80 object-cover rounded-xl border border-slate-100" />
               <div className="mt-4 flex justify-between items-center text-xs font-bold text-[#0F172A]">
                  <span>{project.floorPlans[selectedPlan].title}</span>
                  <span>{project.floorPlans[selectedPlan].area}</span>
               </div>
            </div>
         </div>
      </section>

      {/* Diferenciais da Construtora - STRICT NO SHADOWS */}
      <section className="py-16 px-6 bg-white border-t border-slate-200">
         <div className="max-w-7xl mx-auto">
            <h3 className="text-2xl font-heading font-black text-[#0F172A] mb-8">Diferenciais Construtivos</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
               {project.highlights.map((h, i) => (
                  <div key={i} className="bg-slate-50 p-6 rounded-2xl border border-slate-200">
                     <CheckCircle2 className="w-6 h-6 text-[#10B981] mb-3" />
                     <h5 className="font-bold text-xs text-[#0F172A] leading-relaxed">{h}</h5>
                  </div>
               ))}
            </div>
         </div>
      </section>

    </div>
  );
};
